import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus } from "lucide-react";
import { OBJECTS, VARIANT_PRODUCTS } from "../data/content";
import { useCart } from "../context/CartContext";
import { Reveal } from "./Reveal";
import { Tilt3D } from "./Tilt3D";

const VariantDetail = ({ product, onClose }) => {
  const { addItem } = useCart();
  const [variant, setVariant] = useState(product.variants[0]);
  const [qty, setQty] = useState(1);

  const add = () => {
    addItem({
      lookup_key: product.lookup_key,
      name: `${product.name} — ${variant.label}`,
      price: product.price,
      wash: product.base,
      size: variant.label,
      designImg: variant.img,
      quantity: qty,
    });
    onClose();
  };

  return (
    <motion.div className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-bds-bg/92 backdrop-blur-xl" onClick={onClose} />
      <motion.div data-testid="object-detail"
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.5 }}
        className="relative w-full max-w-6xl max-h-[92vh] overflow-y-auto grid grid-cols-1 md:grid-cols-[1.15fr_1fr] gap-10 md:gap-16 px-4 md:px-8">
        <button onClick={onClose} data-testid="object-detail-close"
          className="absolute top-2 right-2 md:top-4 md:right-4 z-10 w-10 h-10 grid place-items-center text-bds-muted hover:text-bds-fg transition-colors"><X size={18} /></button>

        <div className="pt-6 md:pt-16">
          <Tilt3D max={8} glare={0.25} className="w-full">
            <div className="overflow-hidden aspect-square flex items-center justify-center">
              <img key={variant.img} src={variant.img} alt={variant.label} className="w-full h-full object-cover animate-[fadeIn_0.4s_ease] drop-shadow-[0_40px_80px_rgba(0,0,0,0.6)]" />
            </div>
          </Tilt3D>
          <div className="grid grid-cols-4 gap-3 mt-4">
            {product.variants.map((v) => (
              <button key={v.id} onClick={() => setVariant(v)} data-testid={`variant-${v.id}`}
                className={`overflow-hidden transition-opacity ${variant.id === v.id ? "opacity-100 ring-1 ring-bds-accent" : "opacity-50 hover:opacity-100"}`}>
                <img src={v.img} alt={v.label} className="w-full h-16 object-cover" />
              </button>
            ))}
          </div>
        </div>

        <div className="pt-6 md:pt-16 md:pr-6">
          <div className="flex items-center gap-4 mb-5">
            <span className="h-px w-8 bg-bds-accent" />
            <p className="font-mono-bds text-[10px] uppercase tracking-[0.35em] text-bds-accent">Ritual Objects</p>
          </div>
          <h3 className="font-serif-display font-light text-4xl md:text-5xl leading-[0.95] mb-3">{product.name}</h3>
          <p className="font-mono-bds text-[10px] uppercase tracking-[0.28em] text-bds-muted mb-8">{product.base}</p>

          <p className="font-mono-bds text-[10px] uppercase tracking-[0.28em] text-bds-muted mb-4">Style — {variant.label}</p>
          <div className="flex flex-wrap gap-1.5 mb-10">
            {product.variants.map((v) => (
              <button key={v.id} onClick={() => setVariant(v)} data-testid={`style-${v.id}`}
                className={`min-w-[6rem] h-11 px-4 font-mono-bds text-[11px] uppercase tracking-[0.15em] transition-all ${variant.id === v.id ? "bg-bds-fg text-bds-bg" : "text-bds-muted hover:text-bds-fg"}`}>
                {v.label}
              </button>
            ))}
          </div>

          <p className="font-serif-display text-4xl mb-10">NZ${product.price}</p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-4 text-bds-muted">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-8 h-8 grid place-items-center hover:text-bds-fg transition-colors"><Minus size={14} /></button>
              <span className="w-6 text-center font-mono-bds text-bds-fg" data-testid="obj-qty">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="w-8 h-8 grid place-items-center hover:text-bds-fg transition-colors"><Plus size={14} /></button>
            </div>
            <button onClick={add} data-testid="object-add-to-cart-btn"
              className="group/add flex-1 relative inline-flex items-center justify-between gap-3 font-mono-bds text-xs uppercase tracking-[0.3em] h-14 px-6 bg-bds-fg text-bds-bg hover:bg-bds-accent hover:text-bds-cream transition-colors">
              <span>Add — NZ${product.price * qty}</span>
              <span className="transition-transform duration-500 group-hover/add:translate-x-1">&rarr;</span>
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Objects = () => {
  const { addItem } = useCart();
  const [active, setActive] = useState(null);

  const quickAdd = (o) =>
    addItem({ lookup_key: o.lookup_key, name: o.name, price: o.price, wash: o.type, size: o.size, designImg: o.img, quantity: 1 });

  return (
    <section id="objects" data-testid="objects" className="relative py-28 md:py-44 border-t border-bds-border">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-24">
            <div>
              <span className="font-mono-bds text-xs uppercase tracking-[0.3em] text-bds-accent">Ritual Objects</span>
              <h2 className="font-serif-display font-light text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9] mt-6">
                Carry the <span className="italic">ritual</span>
              </h2>
            </div>
            <p className="font-grotesk text-bds-muted max-w-sm leading-relaxed">
              Caps and socks in four marks — emblem, wordmark, logo, or an art panel — plus candles and incense for the mind&apos;s quiet corners. NZD.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
          {VARIANT_PRODUCTS.map((p, i) => (
            <Reveal key={p.lookup_key} delay={i * 0.06}>
              <div data-testid={`object-${p.lookup_key}`} className="group flex flex-col h-full">
                <Tilt3D max={10} glare={0.3} className="w-full">
                  <button onClick={() => setActive(p)} data-cursor="art" data-testid={`view-${p.lookup_key}`}
                    className="relative block w-full aspect-[4/5] overflow-hidden text-left">
                    <img src={p.card} alt={p.name} className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]" />
                    <span className="absolute top-4 left-4 font-mono-bds text-[10px] uppercase tracking-[0.3em] text-bds-cream/85 mix-blend-difference">0{i + 1}</span>
                    <span className="absolute bottom-4 right-4 font-mono-bds text-[9px] uppercase tracking-[0.25em] text-bds-cream/85 mix-blend-difference">{p.variants.length} styles</span>
                  </button>
                </Tilt3D>
                <div className="pt-6 flex-1 flex flex-col">
                  <h3 className="font-serif-display font-light text-2xl md:text-3xl tracking-tight leading-none">{p.name}</h3>
                  <p className="font-mono-bds text-[10px] uppercase tracking-[0.28em] text-bds-muted mt-3">Emblem · Wordmark · Logo · Art</p>
                  <div className="mt-auto pt-6 flex items-center justify-between gap-3">
                    <span className="font-serif-display text-xl md:text-2xl">NZ${p.price}</span>
                    <button onClick={() => setActive(p)} data-testid={`choose-${p.lookup_key}`}
                      className="group/link inline-flex items-center gap-2 font-mono-bds text-[10px] uppercase tracking-[0.3em] pb-1 border-b border-bds-muted text-bds-muted hover:text-bds-fg hover:border-bds-fg transition-colors">
                      <span>Choose Style</span>
                      <span className="transition-transform duration-500 group-hover/link:translate-x-1">&rarr;</span>
                    </button>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}

          {OBJECTS.map((o, i) => (
            <Reveal key={o.lookup_key} delay={(i + 2) * 0.06}>
              <div data-testid={`object-${o.lookup_key}`} className="group flex flex-col h-full">
                <Tilt3D max={10} glare={0.3} className="w-full">
                  <div className="relative block w-full aspect-[4/5] overflow-hidden">
                    <img src={o.img} alt={o.name} className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]" />
                    <span className="absolute top-4 left-4 font-mono-bds text-[10px] uppercase tracking-[0.3em] text-bds-cream/85 mix-blend-difference">0{i + 3}</span>
                  </div>
                </Tilt3D>
                <div className="pt-6 flex-1 flex flex-col">
                  <h3 className="font-serif-display font-light text-2xl md:text-3xl tracking-tight leading-none">{o.name}</h3>
                  <p className="font-mono-bds text-[10px] uppercase tracking-[0.28em] text-bds-accent mt-3">{o.type}</p>
                  <p className="font-grotesk text-sm text-bds-muted mt-4 leading-relaxed">{o.blurb}</p>
                  <div className="mt-auto pt-6 flex items-center justify-between gap-3">
                    <span className="font-serif-display text-xl md:text-2xl">NZ${o.price}</span>
                    <button onClick={() => quickAdd(o)} data-testid={`add-object-${o.lookup_key}`}
                      className="group/link inline-flex items-center gap-2 font-mono-bds text-[10px] uppercase tracking-[0.3em] pb-1 border-b border-bds-muted text-bds-muted hover:text-bds-fg hover:border-bds-fg transition-colors">
                      <span>Add</span>
                      <span className="transition-transform duration-500 group-hover/link:translate-x-1">&rarr;</span>
                    </button>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && <VariantDetail product={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
};
